import React, { useCallback, useEffect, useState } from "react";
import {
  TrendingUp, Landmark, ClipboardCheck, Route as RouteIcon, Users,
  ShieldCheck, Loader2, CheckCircle2, X, Info, Lock, ArrowUpRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ModuleHeader } from "../components/intel/IntelKit";

const CONSENT_TEXT =
  "By continuing, you authorize C-ORE to securely share your business profile, " +
  "financial information, and business intelligence available within C-ORE with " +
  "our verified funding advisory partners solely for evaluating your funding requirements.";

const SUPPORT = [
  { icon: TrendingUp,     title: "Equity Funding",               desc: "Angel, VC, Growth Capital, Private Equity" },
  { icon: Landmark,       title: "Debt Funding",                 desc: "Working Capital, Term Loans, Venture Debt, Equipment Finance" },
  { icon: ClipboardCheck, title: "Investment Readiness Assessment", desc: "Assess your funding readiness from your existing C-ORE data" },
  { icon: RouteIcon,      title: "Fundraising Strategy",         desc: "A structured plan aligned to your business trajectory" },
  { icon: Users,          title: "Investor & Lender Introductions", desc: "Introductions to verified advisory partners" },
];

export default function CoreFund() {
  const navigate = useNavigate();
  const [access, setAccess] = useState(null); // null (resolving) | "full" | "locked"
  const [showDialog, setShowDialog] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const loadStatus = useCallback(async () => {
    const res = await api.fundStatus();
    if (!res.ok) return;
    if (res.data?.already_submitted) setSubmitted(true);
    if (res.data?.access) setAccess(res.data.access);
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const openDialog = () => { setConsent(false); setError(""); setShowDialog(true); };

  const submit = async () => {
    if (!consent) return;
    setSubmitting(true); setError("");
    const res = await api.fundApply(true);
    setSubmitting(false);
    if (!res.ok) {
      const d = res.error || "";
      setError(String(d).startsWith("funding_migration_required")
        ? "Funding module isn't fully set up yet. Please try again shortly."
        : (res.error || "Could not submit your request. Please try again."));
      return;
    }
    setShowDialog(false);
    setSubmitted(true);
  };

  return (
    <div className="p-6 md:p-8 space-y-7 max-w-4xl" data-testid="core-fund-page">
      <ModuleHeader
        overline="Partners & Tools"
        title="C-ORE Fund"
      />

      {/* Hero */}
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0F1B2D] to-[#1E3A5F] p-6 md:p-8 text-white" data-testid="fund-hero">
        <h1 className="text-2xl md:text-3xl font-bold leading-tight">
          Helping Businesses Raise Capital with Trusted Advisory Partners
        </h1>
        <p className="mt-3 text-[14px] md:text-[15px] text-slate-200 leading-relaxed max-w-2xl">
          C-ORE Fund helps businesses connect with verified funding advisory partners for equity
          and debt fundraising. Using your existing business intelligence and financial data
          available within C-ORE, our advisors can assess your funding readiness and guide you
          through the fundraising process.
        </p>
        {access === "locked" ? (
          <button
            data-testid="fund-locked-cta"
            onClick={() => navigate("/pricing")}
            className="mt-6 inline-flex items-center gap-3 rounded-xl border border-amber-400/40 bg-white/[0.06] backdrop-blur-sm px-5 py-3 text-left hover:bg-white/[0.1] hover:border-amber-400/60 transition-colors"
          >
            <Lock className="h-4 w-4 shrink-0 text-amber-300" />
            <span className="flex flex-col">
              <span className="text-[14px] font-bold text-white leading-tight">Unlock with Command or Sovereign</span>
              <span className="text-[11.5px] text-slate-300/80 leading-tight mt-0.5">Available with C-ORE Command &amp; Sovereign</span>
            </span>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-amber-300/70" />
          </button>
        ) : (
          <button
            data-testid="fund-apply-cta"
            onClick={openDialog}
            disabled={access === null}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-[14px] font-bold text-white hover:bg-emerald-600 transition-colors disabled:opacity-0">
            Apply for Funding Advisory
          </button>
        )}
      </div>

      {/* Already submitted banner */}
      {submitted && (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-[13.5px] text-emerald-800" data-testid="fund-submitted-banner">
          <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
          <span>Your funding advisory request has been submitted successfully. Our team will contact you shortly.</span>
        </div>
      )}

      {/* Funding Support Includes */}
      <div>
        <h3 className="text-[13px] font-semibold uppercase tracking-wider text-slate-500 mb-3">
          Funding Support Includes
        </h3>
        <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-2 gap-3">
          {SUPPORT.map((s) => (
            <div key={s.title} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
                 data-testid={`fund-support-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
                <s.icon className="h-4 w-4 text-[#1E3A5F]" />
              </span>
              <div>
                <div className="text-[14px] font-semibold text-[#1E3A5F]">{s.title}</div>
                <div className="text-[12.5px] text-slate-500 leading-snug">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance notice */}
      <div className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-[11.5px] text-slate-500 leading-relaxed" data-testid="fund-compliance-notice">
        <Info className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
        <span>
          <b className="text-slate-600">Compliance Notice:</b> C-ORE is not a bank, NBFC, investment
          platform, or regulated financial intermediary. C-ORE only facilitates introductions to
          independent funding advisory partners. All lending, investment, and fundraising decisions
          are made solely by the respective financial institutions, investors, or advisory firms
          after their own due diligence.
        </span>
      </div>

      {/* Consent dialog */}
      {showDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F1B2D]/55 p-4"
             data-testid="fund-consent-dialog" onClick={() => !submitting && setShowDialog(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#1E3A5F]" />
                <span className="text-[15px] font-bold text-[#0F1B2D]">Apply for Funding Advisory</span>
              </div>
              <button data-testid="fund-dialog-close" onClick={() => !submitting && setShowDialog(false)}
                      className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
            </div>

            <div className="px-5 py-5">
              <p className="text-[13px] text-slate-700 leading-relaxed">{CONSENT_TEXT}</p>

              <label className="mt-4 flex items-start gap-2.5 cursor-pointer select-none" data-testid="fund-consent-label">
                <input
                  type="checkbox"
                  data-testid="fund-consent-checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#1E3A5F] focus:ring-[#1E3A5F]"
                />
                <span className="text-[12.5px] text-slate-700">
                  I understand and consent to sharing my business information for funding evaluation.
                </span>
              </label>

              {error && (
                <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700" data-testid="fund-dialog-error">{error}</div>
              )}

              <button
                data-testid="fund-submit-btn"
                disabled={!consent || submitting}
                onClick={submit}
                className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-[14px] font-bold text-white transition-colors ${
                  !consent || submitting ? "bg-slate-300 cursor-not-allowed" : "bg-[#1E3A5F] hover:bg-[#16304d]"
                }`}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
